import { ICategory } from "api/types/_category";
import { Option } from "api/types/_public";
import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from "common/toast";
import DropdownOption from "components/Common/DropdownOption";
import { useRole } from "components/Hooks/UserHooks";
import { useFormik } from "formik";
import { ROLES_FOR_APP, isHavePermissionRole } from "helpers/role";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import { Col, Container, Row, Spinner } from 'reactstrap';
import { addCategoryOfKeywordBoostV2Multi, moveCategoryOfKeywordBoostV2Multi, removeCategoryOfKeywordBoostV2Multi } from "store/thunks";
import * as Yup from "yup";

export interface Props {
    ids?: string[] | number[];
    category_id?: string | number,
    categories: ICategory[],
    type: 'ADD' | 'REMOVE' | 'MOVE',
    triggerRefresh?: (option?: { is_refresh_categories?: boolean, is_clear_option_selected?: boolean }) => void;
}

const MoveCategoryFormV2 = ({
    ids = [],
    category_id = '',
    categories = [],
    type = 'MOVE',
    triggerRefresh
}: Props) => {
    const { t, i18n } = useTranslation();

    const { userPermissions } = useRole();

    const CATEGORY_OPTIONS_LANG: Option[] = categories?.map((item: any) => ({ value: `${item?.id}`, label: item?.name }));

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handleSubmit = async (values: any) => {
        try {
            setIsLoading((_prev) => true);
            const form_data = {
                ids: ids,
                category_id: Number(values?.category?.value || CATEGORY_OPTIONS_LANG[0]?.value)
            };
            const response: any = type === 'ADD' ? await addCategoryOfKeywordBoostV2Multi(form_data) : (type === 'REMOVE' ? await removeCategoryOfKeywordBoostV2Multi(form_data) : await moveCategoryOfKeywordBoostV2Multi(form_data));
            if (response?.code === 200) {
                setIsLoading((_prev) => false);
                toast(
                    `${t("The process has been completed.")}`,
                    CONFIG_OPTION_TOAST_NORMAL
                );
                triggerRefresh && triggerRefresh({ is_clear_option_selected: true });
            } else {
                setIsLoading((_prev) => false);
                toast(`${response?.message || response}`, CONFIG_OPTION_TOAST_ERROR);
            }
        } catch (error: any) {
            setIsLoading((_prev) => false);
            toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
            return error;
        }
    };

    const validationSchema = Yup.object({
        category: Yup.object()
            .shape({
                label: Yup.string(),
                value: Yup.string(),
            })
            .nullable().required(`${t("This is required")}`),
    });

    const formik = useFormik({
        initialValues: {
            category: CATEGORY_OPTIONS_LANG?.find((cate) => String(cate?.value) === String(category_id || CATEGORY_OPTIONS_LANG[0]?.value)),
        },
        validationSchema,
        onSubmit: handleSubmit,
    });

    return (
        <React.Fragment>
            <div>
                <Container>
                    <Row>
                        <Col sm={12}>
                            <form onSubmit={formik.handleSubmit}>
                                <Row>
                                    <Col lg={12} className="px-0">
                                        <div className="mb-3">
                                            <label className="form-label">
                                                {t("Category")}
                                                <span className="text-danger"> *</span>
                                            </label>
                                            <DropdownOption
                                                name="category"
                                                dataList={CATEGORY_OPTIONS_LANG || []}
                                                placeholder={`${t("Category")}...`}
                                                className="dropdown-status-rounded"
                                                classNamePrefix="name-prefix"
                                                initialValue={formik?.values?.category || CATEGORY_OPTIONS_LANG[0]}
                                                onChangeSelect={(event: any) =>
                                                    formik.setFieldValue("category", event)
                                                }
                                            />
                                            {formik.touched.category && formik.errors.category ? (
                                                <div className="text-danger mt-2">
                                                    {formik.errors.category}
                                                </div>
                                            ) : null}
                                        </div>
                                    </Col>
                                    <Col lg={12} className="mt-2 px-0 mb-3">
                                        <div className="hstack gap-2 justify-content-end">
                                            {isHavePermissionRole(ROLES_FOR_APP.KEYWORD_BOOST_UPDATE, userPermissions) && (
                                                <button
                                                    style={{ width: '180px' }}
                                                    type="submit"
                                                    className="btn btn-primary fs-14 rounded-pill">
                                                    {isLoading ? <Spinner size="sm me-2" ></Spinner> : <></>}
                                                    {type === 'ADD' ? t("Button Add Category") : (type === 'REMOVE' ? t("Button Remove Category") : t("Button Move"))}
                                                </button>)}
                                        </div>
                                    </Col>
                                </Row>
                            </form>
                        </Col >
                    </Row >
                </Container >
            </div>
        </React.Fragment >
    );
};

export default MoveCategoryFormV2;
